import React, { useState } from "react";
import Card from "../../components/Card";
import Action from "../../components/Action";
import RadioQuestion from "../../components/RadioQuestion";
import ScrollToTop from "../../components/utils/ScrollToTop";
import TextQuestion from "../../components/TextQuestion";
import TrustpilotBanner from "../../components/TrustpilotBanner";
import { formatMoneyWithCurrency, normaliseMoney, isMoneyValid } from "../../shared/utils/moneyUtils";
import { parseTenure } from "../../utils/queryStringUtils";
import { TENURE_OPTIONS, YES_NO_OPTIONS, INSURANCE_LIMIT, SELLING_REJECTION_OPTIONS } from "../../constants/index";
import QuoteLandingPageCopy from "../Quote/QuoteLandingPageCopy";
import ReferralCodeControl, { referrerCodeCopy } from "./ReferralCodeControl";
import ReferralCodeBanner from "../../components/ReferralCodeBanner";
import { isConnellsReferral } from "../ReferrerQuote/utils/ReferrerQuoteUtils";
import { getQuoteEnabled } from "./utils/api";
import AnyOfTheseQuestion from "../../components/AnyOfTheseQuestion";

// Shared ownership, unregistered land and auction come from sign-up’s list, so both ask them the same way
const CHECKLIST_OPTIONS = [
  { name: "divorceOrSeparation", title: "Selling because of a divorce or separation" },
  { name: "retirementProperty", title: "Selling a retirement or later-living home, such as sheltered housing" },
  ...SELLING_REJECTION_OPTIONS,
];

const QuoteSellingProperty = (props) => {
  const [fields, setFields] = useState(() => {
    if (props.sellingDetails) {
      const sd = props.sellingDetails;
      return {
        price: formatMoneyWithCurrency(sd.price),
        tenure: sd.tenure,
        hasMortgage: sd.hasMortgage,
        divorceOrSeparation: sd.divorceOrSeparation,
        retirementProperty: sd.retirementProperty,
        sharedOwnership: sd.sharedOwnership,
        unregisteredLand: sd.unregisteredLand,
        auctionSale: sd.auctionSale,
      };
    } else {
      return {
        price: "",
        tenure: parseTenure(),
        hasMortgage: null,
        divorceOrSeparation: null,
        retirementProperty: null,
        sharedOwnership: null,
        unregisteredLand: null,
        auctionSale: null,
      };
    }
  });
  const setField = (name) => (value) => {
    setFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [errors, setErrors] = useState({});
  const setError = (field, isError) => {
    setErrors((prev) => ({
      ...prev,
      [field]: isError,
    }));
  };

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const [referral, setReferral] = useState(props.referral);

  const showMortgageQuestion = !!props.referral && isConnellsReferral(props.referral.referralSource);

  const handleFieldValidation = (field) => (event) => {
    if (event.type !== "blur") {
      setError(field, false);
    } else {
      if (!fields[field]) {
        setError(field, true);
      }
    }
  };

  const handlePriceFieldValidation = (event) => {
    if (event.type !== "blur") {
      setError("price", false);
    } else {
      if (!fields["price"] || !isMoneyValid(fields["price"])) {
        setError("price", true);
      }
    }
  };

  const handleValidation = () => {
    let newErrors = {};
    let formIsValid = true;

    if (fields["price"] == null || !isMoneyValid(fields["price"])) {
      newErrors["price"] = true;
      formIsValid = false;
    }

    if (fields["tenure"] == null) {
      newErrors["tenure"] = true;
      formIsValid = false;
    }

    if (showMortgageQuestion && fields["hasMortgage"] == null) {
      newErrors["hasMortgage"] = true;
      formIsValid = false;
    }

    if (CHECKLIST_OPTIONS.some((option) => fields[option.name] == null)) {
      newErrors["checklist"] = true;
      formIsValid = false;
    }

    setErrors(newErrors);
    return formIsValid;
  };

  const loadRejectionReasons = (onLoad) => {
    let reasons = [];

    if (normaliseMoney(fields.price) >= INSURANCE_LIMIT) {
      reasons.push("price_too_high");
    }

    if (!!fields.sharedOwnership) {
      reasons.push("shared_ownership");
    }
    if (!!fields.unregisteredLand) {
      reasons.push("unregistered_land");
    }
    if (!!fields.auctionSale) {
      reasons.push("auction");
    }
    if (!!fields.divorceOrSeparation) {
      reasons.push("divorce_or_separation");
    }

    if (reasons.length > 0) {
      // Skip loading additional reasons if we already have some
      onLoad(reasons);
      return;
    }

    setLoading(true);
    getQuoteEnabled({
      source: referral?.referralSource || props.defaultChannel,
      transactionType: "selling",
      tenure: fields.tenure,
      onLoaded: (enabled, additionalReasons) => {
        if (!enabled) {
          reasons.push(...additionalReasons);
        }
        setLoading(false);
        onLoad(reasons);
      },
      onError: (err) => {
        setLoadError("Unable to generate your quote");
        setLoading(false);
      },
    });
  };

  const saveSelection = () => {
    if (!handleValidation()) {
      return;
    }
    loadRejectionReasons((reasons) => {
      props.onStepComplete({
        sellingDetails: {
          tenure: fields.tenure,
          price: normaliseMoney(fields.price),
          hasMortgage: fields.hasMortgage,
          divorceOrSeparation: fields.divorceOrSeparation,
          retirementProperty: fields.retirementProperty,
          sharedOwnership: fields.sharedOwnership,
          unregisteredLand: fields.unregisteredLand,
          auctionSale: fields.auctionSale,
        },
        sellingRejectionReasons: reasons,
        referral: referral,
      });
    });
  };

  if (!!loadError) {
    return (
      <React.Fragment>
        <ScrollToTop />
        <Card title="Sorry, that didn't work" removeTopPadding>
          {loadError}
        </Card>
      </React.Fragment>
    );
  }

  let cardTitle;
  let priceHint;
  let buttonText;

  if (props.mode === "direct") {
    cardTitle = "About the home you’re selling";
    priceHint = "An estimate is fine. You can change it later.";

    buttonText =
      props.transactionType === "BuyingAndSelling" ? "Continue to the home you’re buying" : "Next: your details";
  } else {
    cardTitle = "About the home your client is selling";
    priceHint = "The asking or agreed price. An estimate is fine.";

    buttonText = "Continue";
  }

  return (
    <React.Fragment>
      <ScrollToTop />

      {props.showLandingCopy && <QuoteLandingPageCopy pageWidth={props.pageWidth} />}

      <Card title={cardTitle} removeTopPadding={true}>
        <TextQuestion
          question="Sale price"
          hint={priceHint}
          inputMode="numeric"
          value={fields.price}
          valueEntered={setField("price")}
          validate={handlePriceFieldValidation}
          validationError={errors.price ? "Enter the sale price, for example 350000" : null}
        />

        {showMortgageQuestion && (
          <RadioQuestion
            question="Do they have a mortgage?"
            options={YES_NO_OPTIONS}
            inline={true}
            name="hasMortgage"
            selected={fields.hasMortgage}
            optionSelected={setField("hasMortgage")}
            validate={handleFieldValidation("hasMortgage")}
            validationError={errors["hasMortgage"] ? "Choose Yes or No" : null}
          />
        )}

        <RadioQuestion
          question="Is the property freehold or leasehold?"
          options={TENURE_OPTIONS}
          name="tenure"
          selected={fields.tenure}
          optionSelected={setField("tenure")}
          validate={handleFieldValidation("tenure")}
          validationError={errors.tenure ? "Choose freehold or leasehold" : null}
        />

        <AnyOfTheseQuestion
          options={CHECKLIST_OPTIONS}
          fields={fields}
          onChange={(answers) => {
            setFields((prev) => ({ ...prev, ...answers }));
            setError("checklist", false);
          }}
          invalid={!!errors["checklist"]}
        />

        {!!props.referral && !props.hideReferralCodeBanner && (
          <ReferralCodeBanner text={props.referral.discountMessage} small={true} />
        )}

        {props.transactionType === "Selling" && !props.referralLocked && (
          <ReferralCodeControl
            audience={props.mode === "referrer" ? "referrer" : null}
            {...(props.mode === "referrer" ? referrerCodeCopy : {})}
            referral={referral}
            onReferralCodeApplied={setReferral}
          />
        )}

        <Action onClick={saveSelection} disabled={loading}>
          {buttonText}
        </Action>
      </Card>

      <TrustpilotBanner pageWidth={props.pageWidth} />
    </React.Fragment>
  );
};

export default QuoteSellingProperty;
